/**
 * Camera screen — pill identification via device camera.
 * Shows a viewfinder, capture button, and result display.
 */

import type { PillIdResult } from '../../types/index';
import { speak } from '../../voice/tts';
import { showNotification } from '../components/notification-card';
import { t } from '../../i18n';

// -----------------------------------------------------------------------
// Camera helpers
// -----------------------------------------------------------------------

async function startCamera(video: HTMLVideoElement): Promise<MediaStream | null> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment', // rear camera preferred
        width: { ideal: 1280 },
        height: { ideal: 960 },
      },
    });
    video.srcObject = stream;
    await video.play();
    return stream;
  } catch {
    return null;
  }
}

function stopCamera(stream: MediaStream | null): void {
  if (stream) {
    for (const track of stream.getTracks()) {
      track.stop();
    }
  }
}

function captureFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// -----------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------

export async function renderCameraScreen(container: HTMLElement): Promise<void> {
  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col gap-md';
  wrapper.style.maxWidth = '600px';
  wrapper.style.margin = '0 auto';

  // Header
  const header = document.createElement('header');
  header.className = 'screen-header';
  const title = document.createElement('h2');
  title.textContent = t('camera.title');
  header.appendChild(title);
  wrapper.appendChild(header);

  // Instructions
  const instructions = document.createElement('p');
  instructions.className = 'text-muted text-center mb-md';
  instructions.textContent = t('camera.instruction');
  instructions.style.fontSize = 'var(--font-size-large)';
  wrapper.appendChild(instructions);

  // Camera viewfinder
  const viewfinder = document.createElement('div');
  viewfinder.className = 'camera-viewfinder';
  viewfinder.setAttribute('role', 'img');
  viewfinder.setAttribute('aria-label', t('camera.title'));

  const video = document.createElement('video');
  video.setAttribute('playsinline', '');
  video.setAttribute('autoplay', '');
  video.muted = true;
  video.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
  viewfinder.appendChild(video);

  // Crosshair overlay
  const overlay = document.createElement('div');
  overlay.className = 'camera-overlay';
  overlay.setAttribute('aria-hidden', 'true');

  const crosshair = document.createElement('div');
  crosshair.className = 'camera-crosshair';
  overlay.appendChild(crosshair);
  viewfinder.appendChild(overlay);

  wrapper.appendChild(viewfinder);

  // Camera status
  const statusEl = document.createElement('p');
  statusEl.className = 'text-dim text-center mt-sm';
  statusEl.setAttribute('aria-live', 'polite');
  statusEl.textContent = t('camera.starting');
  wrapper.appendChild(statusEl);

  // Capture button
  const captureBtn = document.createElement('button');
  captureBtn.className = 'btn btn-primary btn-lg btn-block mt-md';
  captureBtn.textContent = t('camera.capture');
  captureBtn.setAttribute('aria-label', t('camera.capture'));
  captureBtn.disabled = true;
  wrapper.appendChild(captureBtn);

  // Result area
  const resultArea = document.createElement('div');
  resultArea.className = 'flex-col gap-md mt-md';
  resultArea.setAttribute('role', 'region');
  resultArea.setAttribute('aria-label', t('camera.title'));
  resultArea.setAttribute('aria-live', 'polite');
  wrapper.appendChild(resultArea);

  container.appendChild(wrapper);

  // Initialize camera
  let stream: MediaStream | null = null;
  stream = await startCamera(video);

  if (stream) {
    statusEl.textContent = t('camera.ready');
    captureBtn.disabled = false;
  } else {
    statusEl.textContent = t('camera.noAccess');
    statusEl.className = 'text-danger text-center mt-sm';
    showNotification(t('camera.noAccessNotify'), 'error');
    speak(t('camera.noAccessSpeak'));
  }

  // Capture handler
  captureBtn.addEventListener('click', () => {
    if (!stream) return;

    captureBtn.disabled = true;
    captureBtn.textContent = t('camera.analyzing');
    statusEl.textContent = t('camera.analyzingStatus');

    const canvas = captureFrame(video);
    resultArea.textContent = '';

    // Show the captured image
    const capturedImg = document.createElement('img');
    capturedImg.src = canvas.toDataURL('image/jpeg', 0.8);
    capturedImg.alt = t('camera.capturedAlt');
    capturedImg.style.cssText = 'width: 100%; border-radius: var(--radius-md);';
    resultArea.appendChild(capturedImg);

    // Dispatch event for pill identification processing
    // The actual ML model integration will handle this event
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    document.dispatchEvent(
      new CustomEvent('aftercare:pill-capture', { detail: { imageData } }),
    );

    // Listen for identification result
    const onResult = (e: Event): void => {
      const result = (e as CustomEvent).detail as PillIdResult | null;
      document.removeEventListener('aftercare:pill-result', onResult);

      captureBtn.disabled = false;
      captureBtn.textContent = t('camera.captureAgain');

      if (result && result.confidence > 0.5) {
        renderPillResult(resultArea, result);
        statusEl.textContent = t('camera.identifiedStatus');
        speak(t('camera.looksLikeSpeak', { drug: result.drug_name, confidence: String(Math.round(result.confidence * 100)) }));
      } else {
        renderNoMatch(resultArea);
        statusEl.textContent = t('camera.couldNotIdentifyStatus');
        speak(t('camera.couldNotIdentifySpeak'));
      }
    };

    document.addEventListener('aftercare:pill-result', onResult);

    // Timeout fallback — if no result in 10s, show no match
    setTimeout(() => {
      document.removeEventListener('aftercare:pill-result', onResult);
      if (captureBtn.textContent === t('camera.analyzing')) {
        captureBtn.disabled = false;
        captureBtn.textContent = t('camera.captureAgain');
        renderNoMatch(resultArea);
        statusEl.textContent = t('camera.timeoutStatus');
      }
    }, 10000);
  });

  // Clean up camera when navigating away
  const onHashChange = (): void => {
    if (!window.location.hash.includes('pill-camera')) {
      stopCamera(stream);
      window.removeEventListener('hashchange', onHashChange);
    }
  };
  window.addEventListener('hashchange', onHashChange);
}

// -----------------------------------------------------------------------
// Result rendering
// -----------------------------------------------------------------------

function renderPillResult(container: HTMLElement, result: PillIdResult): void {
  // Keep any captured image that might already be in the container
  const existingImg = container.querySelector('img');

  const card = document.createElement('div');
  card.className = 'card';
  card.style.padding = 'var(--space-lg)';

  const heading = document.createElement('h3');
  heading.className = 'text-center mb-md';
  heading.textContent = t('camera.looksLike');
  card.appendChild(heading);

  const nameEl = document.createElement('div');
  nameEl.className = 'text-center';
  nameEl.style.cssText = 'font-size: var(--font-size-2xl); font-weight: 700; color: var(--color-text); margin-bottom: var(--space-md);';
  nameEl.textContent = result.drug_name;
  card.appendChild(nameEl);

  // Confidence
  const confEl = document.createElement('div');
  confEl.className = 'text-center mb-md';
  const confPct = Math.round(result.confidence * 100);
  const confClass = confPct >= 80 ? 'badge-success' : confPct >= 60 ? 'badge-warning' : 'badge-danger';
  const confBadge = document.createElement('span');
  confBadge.className = 'badge ' + confClass;
  confBadge.textContent = t('camera.confidence', { n: confPct });
  confEl.appendChild(confBadge);
  card.appendChild(confEl);

  // Details
  const details: [string, string][] = [];
  if (result.color) details.push([t('camera.color'), result.color]);
  if (result.shape) details.push([t('camera.shape'), result.shape]);
  if (result.imprint) details.push([t('camera.imprint'), result.imprint]);

  if (details.length > 0) {
    for (const [label, value] of details) {
      const row = document.createElement('div');
      row.style.cssText = 'display: flex; justify-content: space-between; padding: var(--space-xs) 0; border-bottom: 1px solid var(--color-border);';

      const labelEl = document.createElement('span');
      labelEl.className = 'text-muted';
      labelEl.textContent = label;
      row.appendChild(labelEl);

      const valueEl = document.createElement('span');
      valueEl.textContent = value;
      row.appendChild(valueEl);

      card.appendChild(row);
    }
  }

  // View full info button
  const viewBtn = document.createElement('button');
  viewBtn.className = 'btn btn-primary btn-block mt-lg';
  viewBtn.textContent = t('camera.viewDrugInfo');
  viewBtn.setAttribute('aria-label', t('camera.viewDrugInfo'));
  viewBtn.addEventListener('click', () => {
    // Navigate to drug info — the app router will handle this
    window.location.hash = '#drug/' + encodeURIComponent(result.drug_name);
  });
  card.appendChild(viewBtn);

  // Disclaimer
  const disclaimer = document.createElement('p');
  disclaimer.className = 'text-dim text-center mt-md';
  disclaimer.style.fontSize = 'var(--font-size-small)';
  disclaimer.textContent = t('camera.disclaimer');
  card.appendChild(disclaimer);

  if (existingImg) {
    container.textContent = '';
    container.appendChild(existingImg);
  }
  container.appendChild(card);
}

function renderNoMatch(container: HTMLElement): void {
  const existingImg = container.querySelector('img');

  const card = document.createElement('div');
  card.className = 'card text-center';
  card.style.padding = 'var(--space-lg)';

  const icon = document.createElement('div');
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '\u2753'; // question mark
  icon.style.fontSize = '48px';
  card.appendChild(icon);

  const msg = document.createElement('p');
  msg.className = 'text-muted mt-md';
  msg.style.fontSize = 'var(--font-size-large)';
  msg.textContent = t('camera.couldNotIdentify');
  card.appendChild(msg);

  const tips = document.createElement('div');
  tips.className = 'text-dim mt-md';
  tips.style.textAlign = 'left';

  const tipTitle = document.createElement('strong');
  tipTitle.textContent = t('camera.tips');
  tips.appendChild(tipTitle);

  const tipList = document.createElement('ul');
  tipList.style.paddingLeft = 'var(--space-lg)';
  const tipItems = [
    t('camera.tip1'),
    t('camera.tip2'),
    t('camera.tip3'),
    t('camera.tip4'),
  ];
  for (const tip of tipItems) {
    const li = document.createElement('li');
    li.textContent = tip;
    li.style.marginBottom = 'var(--space-xs)';
    tipList.appendChild(li);
  }
  tips.appendChild(tipList);
  card.appendChild(tips);

  if (existingImg) {
    container.textContent = '';
    container.appendChild(existingImg);
  }
  container.appendChild(card);
}
